export const MOBILE_PAYMENT_CONTAINER_ID = 'cirenioMobilePaymentContainer';

let scriptLoadPromise = null;

export const isMobileAuthError = (error) => (
  error?.mobileAuthError === true || error?.response?.status === 401
);

export const getMobileAuthErrorMessage = (error) => {
  const errorType = error?.mobileAuthErrorType
    || (error?.response?.data?.message?.includes('expired') ? 'token_expired' : 'invalid_token');

  if (errorType === 'token_expired') {
    return 'Tu sesión expiró. Recargá la página o pedile a tu vendedor un nuevo enlace.';
  }

  return 'Tu sesión ya no es válida. Recargá la página o pedile a tu vendedor un nuevo enlace.';
};

export const reportMobileAuthError = (error, onSessionExpired) => {
  if (!isMobileAuthError(error)) {
    return false;
  }

  onSessionExpired?.(error);
  return true;
};

export const reloadCurrentPage = () => {
  window.location.reload();
};

const PAYMENT_WIDGET_CLOSE_BUTTON_STYLE_ID = 'mobile-payment-widget-close-button-styles';

export const applyPaymentWidgetCloseButtonStyles = () => {
  if (!document.getElementById(PAYMENT_WIDGET_CLOSE_BUTTON_STYLE_ID)) {
    const style = document.createElement('style');
    style.id = PAYMENT_WIDGET_CLOSE_BUTTON_STYLE_ID;
    style.textContent = `
      #cirenio-modal #cirenio-close-button {
        align-items: center !important;
        background: rgba(255, 255, 255, 0.96) !important;
        border: 1px solid rgba(108, 117, 125, 0.35) !important;
        border-radius: 50% !important;
        box-shadow: 0 2px 8px rgba(15, 23, 42, 0.18) !important;
        color: #495057 !important;
        display: flex !important;
        font-size: 28px !important;
        height: 40px !important;
        justify-content: center !important;
        line-height: 1 !important;
        position: fixed !important;
        right: 12px !important;
        top: 12px !important;
        width: 40px !important;
        z-index: 100000 !important;
      }
    `;
    document.head.appendChild(style);
  }

  const closeButton = document.getElementById('cirenio-close-button');
  if (!closeButton) {
    return false;
  }

  closeButton.style.setProperty('position', 'fixed', 'important');
  closeButton.style.setProperty('top', '12px', 'important');
  closeButton.style.setProperty('right', '12px', 'important');
  closeButton.style.setProperty('z-index', '100000', 'important');
  closeButton.style.setProperty('color', '#495057', 'important');
  closeButton.style.setProperty('background', 'rgba(255, 255, 255, 0.96)', 'important');
  closeButton.style.setProperty('border', '1px solid rgba(108, 117, 125, 0.35)', 'important');
  closeButton.style.setProperty('border-radius', '50%', 'important');
  closeButton.style.setProperty('width', '40px', 'important');
  closeButton.style.setProperty('height', '40px', 'important');
  closeButton.style.setProperty('display', 'flex', 'important');
  closeButton.style.setProperty('align-items', 'center', 'important');
  closeButton.style.setProperty('justify-content', 'center', 'important');
  closeButton.style.setProperty('box-shadow', '0 2px 8px rgba(15, 23, 42, 0.18)', 'important');

  return true;
};

export const watchPaymentWidgetCloseButton = () => {
  if (applyPaymentWidgetCloseButtonStyles()) {
    return () => {};
  }

  const observer = new MutationObserver(() => {
    if (applyPaymentWidgetCloseButtonStyles()) {
      observer.disconnect();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  const timeoutId = setTimeout(() => {
    observer.disconnect();
  }, 10000);

  return () => {
    observer.disconnect();
    clearTimeout(timeoutId);
  };
};

export const clearPaymentWidgetContainer = (containerId = MOBILE_PAYMENT_CONTAINER_ID) => {
  const container = document.getElementById(containerId);
  if (container) {
    container.innerHTML = '';
  }
};

export const cleanupPaymentWidgetArtifacts = (containerId = MOBILE_PAYMENT_CONTAINER_ID) => {
  clearPaymentWidgetContainer(containerId);

  document.querySelectorAll('body > iframe').forEach((iframe) => {
    const src = (iframe.getAttribute('src') || '').toLowerCase();
    if (src.includes('cirenio') || src.includes('payment') || src.includes('psp')) {
      iframe.remove();
    }
  });

  document.querySelectorAll('body > div').forEach((node) => {
    const id = (node.id || '').toLowerCase();
    const className = typeof node.className === 'string' ? node.className.toLowerCase() : '';
    const style = node.getAttribute('style') || '';

    const looksLikePaymentOverlay = (
      id.includes('cirenio')
      || id.includes('payment')
      || className.includes('cirenio')
      || className.includes('payment-widget')
    ) && style.includes('fixed');

    if (looksLikePaymentOverlay) {
      node.remove();
    }
  });
};

export const loadCirenioPaymentScript = (scriptSrc) => {
  if (!scriptSrc) {
    return Promise.reject(new Error('Missing payment SDK source'));
  }

  if (window.CirenioPayments) {
    return Promise.resolve();
  }

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector(`script[src="${scriptSrc}"]`);

    if (existingScript) {
      if (window.CirenioPayments) {
        resolve();
        return;
      }

      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => {
        scriptLoadPromise = null;
        reject(new Error('Failed to load payment SDK'));
      }, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    script.dataset.cirenioPaymentSdk = 'true';
    script.onload = () => resolve();
    script.onerror = () => {
      scriptLoadPromise = null;
      reject(new Error('Failed to load payment SDK'));
    };
    document.body.appendChild(script);
  });

  return scriptLoadPromise;
};

export const destroyPaymentsInstance = (paymentsInstance, containerId = MOBILE_PAYMENT_CONTAINER_ID) => {
  if (paymentsInstance) {
    paymentsInstance.destroy?.();
    paymentsInstance.closeWidget?.();
  }

  cleanupPaymentWidgetArtifacts(containerId);
};

const containerHasInteractiveWidget = (container) => {
  if (!container) {
    return false;
  }

  if (container.querySelector('input, select, textarea')) {
    return true;
  }

  return !!container.querySelector('iframe');
};

export const waitForPaymentWidgetContent = (
  containerId = MOBILE_PAYMENT_CONTAINER_ID,
  timeoutMs = 10000,
) => new Promise((resolve, reject) => {
  const container = document.getElementById(containerId);

  if (!container) {
    reject(new Error('Payment widget container not found'));
    return;
  }

  let isSettled = false;
  let observer = null;
  let timeoutId = null;

  const cleanup = () => {
    observer?.disconnect();
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };

  const settle = (callback) => {
    if (isSettled) return;
    isSettled = true;
    cleanup();
    callback();
  };

  const getIframe = () => container.querySelector('iframe');

  const waitForIframeLoad = (iframe) => new Promise((resolveIframe) => {
    if (!iframe) {
      resolveIframe();
      return;
    }

    const finish = () => {
      setTimeout(() => {
        requestAnimationFrame(() => resolveIframe());
      }, 300);
    };

    iframe.addEventListener('load', finish, { once: true });
    setTimeout(finish, 2000);
  });

  const checkAndResolve = async () => {
    if (!containerHasInteractiveWidget(container)) {
      return false;
    }

    const iframe = getIframe();
    if (iframe) {
      await waitForIframeLoad(iframe);
    } else {
      await new Promise((resolveDelay) => {
        requestAnimationFrame(() => requestAnimationFrame(resolveDelay));
      });
    }

    settle(resolve);
    return true;
  };

  checkAndResolve().then((ready) => {
    if (ready) return;

    observer = new MutationObserver(() => {
      checkAndResolve();
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    timeoutId = setTimeout(async () => {
      const readyOnTimeout = await checkAndResolve();
      if (!readyOnTimeout) {
        settle(() => reject(new Error('Payment widget content timeout')));
      }
    }, timeoutMs);
  });
});
